import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Check, Star, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';
import SignOutButton from '@/components/SignOutButton';

const SummaryPage = () => {
  const { user, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = axiosAuth();
        
        // Fetch summary (only available if all checkpoints complete)
        const summaryRes = await api.get('/reflections/summary/all');
        setReflections(summaryRes.data);
      } catch (error) {
        console.error('Error fetching summary:', error);
        if (error.response?.status === 403) {
          toast.error(copy.summary.incompleteError);
          navigate('/dashboard');
        } else {
          toast.error(copy.summary.loadError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosAuth, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1D2D44] font-medium">{copy.summary.loadingSummary}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="flex items-center text-[#424E59] hover:text-[#1D2D44] transition-colors"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{copy.nav.backToJourney}</span>
          </Link>
          
          <Link to="/dashboard" className="font-serif text-lg text-[#1D2D44]" data-testid="brand-logo-link">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
          </Link>
          
          <div className="flex items-center justify-end w-20 sm:w-auto">
            <SignOutButton />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Completion Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-20 h-20 bg-[#5E7F63] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-3">
            {copy.summary.title}
          </h1>
          <p className="text-[#3A4A5A] max-w-lg mx-auto">
            {copy.summary.congratulations(user?.name?.split(' ')[0])}
          </p>
        </div>

        {/* Summary Content - Print-like style */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 animate-slide-up">
          <div className="bg-[#1D2D44] p-6 text-center">
            <p className="text-[#B89B5E] text-sm uppercase tracking-widest mb-2">{copy.summary.summaryLabel}</p>
            <h2 className="font-serif text-2xl text-white">
              {copy.summary.summaryTitle}
            </h2>
          </div>
          
          <div className="p-6 sm:p-10">
            {reflections.map((reflection, index) => (
              <div 
                key={reflection.id}
                className={`${index > 0 ? 'border-t border-gray-100 pt-8 mt-8' : ''}`}
                data-testid={`summary-reflection-${reflection.checkpoint_id}`}
              >
                {/* Checkpoint Header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full bg-[#1D2D44] flex items-center justify-center text-[#B89B5E] text-sm font-medium">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-[#1D2D44]">{reflection.checkpoint_name}</h3>
                    <p className="text-sm text-[#5E7F63]">{reflection.checkpoint_subtitle}</p>
                  </div>
                </div>

                {/* Serious Reflection */}
                <div className="ml-11">
                  <p className="text-[#3A4A5A] text-sm italic mb-2">
                    {reflection.serious_question}
                  </p>
                  <p className="text-[#1D2D44] leading-relaxed font-serif whitespace-pre-wrap">
                    {reflection.serious_answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profile CTA - replaces the former Founding Member invitation */}
        <Card className="border-none shadow-lg rounded-2xl overflow-hidden mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-6 sm:p-8 text-center">
            <p className="font-serif text-xl text-[#1D2D44] mb-5">
              Ready to build your profile? Your next step is waiting.
            </p>
            <Link to="/profile" data-testid="continue-to-profile-btn">
              <Button
                className="bg-[#1D2D44] text-[#C9A84C] hover:bg-[#1F1F1F] rounded-full px-8 py-5 shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                Continue to Your Profile
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Share Note */}
        <Card className="border-none shadow-sm rounded-2xl bg-[#A3B8A6]/20 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <CardContent className="p-6">
            <h4 className="font-serif text-lg text-[#1D2D44] mb-2">
              {copy.summary.sharingTitle}
            </h4>
            <p className="text-[#3A4A5A] text-sm leading-relaxed">
              {copy.summary.sharingDescription}
            </p>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/reflections">
            <Button 
              variant="outline"
              className="border-[#1D2D44] text-[#1D2D44] hover:bg-[#F5F5F5] rounded-full"
              data-testid="view-reflections-btn"
            >
              {copy.summary.viewReflections}
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button 
              className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full"
              data-testid="back-to-dashboard-btn"
            >
              {copy.summary.backToDashboard}
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#424E59] text-sm">
            {copy.summary.privacyFooter}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SummaryPage;
